import type { AppState } from '../core/types';
import { createMasterClassification } from '../domain/classification';
import { deriveDisplaySet } from '../domain/displaySet';
import { splittableSuerteFields, suerteSplitBlocker, type SuerteSplitPlan } from '../domain/suerteSplit';
import { emptySelectionSet } from '../domain/selectionSet';
import { themeTargetIds } from '../domain/themeTarget';
import type { ClassificationConfig } from './config';
import { quickClassificationConfig, quickFieldOptions } from './quickClassification';

export type SuerteSplitAvailability =
  | { readonly ok: true; readonly suerteId: string; readonly label: string }
  | { readonly ok: false; readonly reason: string };

/** The one logical Suerte the guided split works on, or why the action is not available now. */
export function suerteSplitAvailability(state: AppState): SuerteSplitAvailability {
  const model = state.model;
  if (!model) return { ok: false, reason: 'Carga primero un Maestro CASUR.' };
  if (state.geometryEditSession) return { ok: false, reason: 'Termina la edición de geometría antes de dividir.' };
  if (state.entityViewLevel !== 'suerte') return { ok: false, reason: 'Elige Ver por Suertes y selecciona una Suerte.' };
  const ids = new Set(state.selectionSet.entries.filter((entry) => entry.source === 'master' && entry.kind === 'suerte').map(({ id }) => id));
  if (!ids.size && state.selection?.kind === 'suerte') ids.add(state.selection.id);
  if (ids.size !== 1 || state.selectionSet.entries.some((entry) => entry.source !== 'master' || entry.kind !== 'suerte')) return { ok: false, reason: 'Selecciona una sola Suerte para dividir su estado.' };
  const suerteId = [...ids][0]!; const suerte = model.suertes.get(suerteId);
  if (!suerte) return { ok: false, reason: 'No se encontró la Suerte seleccionada.' };
  const visible = deriveDisplaySet(state).masterFragmentIds;
  if (!suerte.fragmentIds.some((id) => visible.has(id))) return { ok: false, reason: 'La Suerte seleccionada no está visible con el ámbito y los filtros actuales.' };
  const blocker = suerteSplitBlocker(model, suerteId, state.subareas); if (blocker) return { ok: false, reason: blocker };
  const finca = model.fincas.get(suerte.fincaId);
  return { ok: true, suerteId, label: `${finca?.name ?? ''} · Suerte ${suerte.label}`.replace(/^ · /, '') };
}

/** Active categorized Suerte classification of a splittable text field, if any: the guided flow starts from it. */
export function activeSplitField(state: AppState): string | null {
  const config = state.classificationConfig;
  if (!state.model || config.layerId !== state.model.id || config.mode !== 'categorized' || !config.field || state.entityViewLevel !== 'suerte') return null;
  return splittableSuerteFields(state.operationalFields).some(({ descriptor }) => descriptor.id === config.field) ? config.field : null;
}

/**
 * The classification shown right after the split: the compatible active one, or the standard quick classification
 * of the field; in both cases with Áreas parciales as exceptions on the partial field. The divided Suerte is
 * explicitly added to the ThemeTarget: the user asked to classify it by this field (an Área parcial alone never
 * widens the target).
 */
export function classificationAfterSplit(state: AppState, plan: SuerteSplitPlan): ClassificationConfig {
  const model = state.model!; const layer = state.layers.find(({ id }) => id === model.id)!;
  const withFields: AppState = { ...state, operationalFields: plan.fields, subareas: plan.subareas, entityViewLevel: 'suerte' };
  const active = activeSplitField(state) === plan.baseFieldId;
  let config: ClassificationConfig = active ? { ...state.classificationConfig, level: 'suerte' } : quickClassificationConfig(withFields, layer, plan.baseFieldId, 'auto', quickFieldOptions(withFields, layer).find(({ id }) => id === plan.baseFieldId));
  const target = new Set(config.themeTarget.featureIds);
  const missing = model.suertes.get(plan.parent.id)!.fragmentIds.filter((id) => !target.has(id));
  if (missing.length) config = { ...config, themeTarget: { ...config.themeTarget, source: config.themeTarget.source === 'layer' ? 'layer' : 'custom', featureIds: [...config.themeTarget.featureIds, ...missing] } };
  config = { ...config, partialExceptions: { enabled: true, fieldId: plan.partialFieldId } };
  return createMasterClassification(model, config, themeTargetIds(layer, config.themeTarget), plan.fields);
}

/**
 * The single, coherent store update that applies a split: subareas, fields, classification and selection change
 * together or not at all. The selection is cleared so the divided Suerte is visible at once.
 */
export function suerteSplitPatch(state: AppState, plan: SuerteSplitPlan): Partial<AppState> {
  if (state.model !== plan.basis.model || state.subareas !== plan.basis.subareas || state.operationalFields !== plan.basis.fields) {
    throw new Error('El proyecto cambió mientras se preparaba la división. Vuelve a dibujar la parte.');
  }
  const classificationConfig = classificationAfterSplit(state, plan);
  const schemes = state.thematicSchemes.some(({ id }) => id === classificationConfig.id)
    ? state.thematicSchemes.map((item) => item.id === classificationConfig.id ? classificationConfig : item)
    : [...state.thematicSchemes, classificationConfig];
  return {
    subareas: plan.subareas, operationalFields: plan.fields, classificationConfig, legendConfig: classificationConfig.legend, thematicSchemes: schemes,
    selectionSet: emptySelectionSet(), selection: null, genericSelection: null, activeTool: 'select', error: null,
  };
}
